import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Table,
  Space,
  message,
  Select,
  InputNumber,
  Input,
  DatePicker,
  Tabs,
  Spin,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createTariffRule,
  getTariffRules,
  updateTariffRule,
} from "../../../api/tariff/tariffRuleRequest";
import {
  createTariffPeriod,
  getTariffPeriods,
  updateTariffPeriod,
} from "../../../api/tariff/tariffPeriodRequest";
import {
  getAllTariffSubscriptions,
  updateTariffSubscriptions,
} from "../../../api/tariff/tariffSubscriptionRequest";
import FormModal from "../../../components/FormModal";
import CalculateurTemps from "../components/CalculateurTemps";
import { getSubscriptionTypes } from "../../../api/subscription_types/subscriptionTypesRequest";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const getPeriodNamebyId = (id, periods) => {
  const period = periods.find((p) => p.id === id);
  return period ? period.name : "";
};

function TariffTab() {
  const [data, setData] = useState({
    tariffPeriods: [],
    tariffRules: [],
    tariffSubscriptions: [],
    subscriptionTypes: [],
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [periods, rules, types, subscription] = await Promise.all([
        getTariffPeriods(),
        getTariffRules(),
        getSubscriptionTypes(),
        getAllTariffSubscriptions(),
      ]);
      setData({
        tariffPeriods: periods,
        tariffRules: rules,
        subscriptionTypes: types,
        tariffSubscriptions: subscription,
      });
    } catch (error) {
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionTypeNamebyId = (id) => {
    const subscriptionType = data.subscriptionTypes.find((s) => s.id === id);
    return subscriptionType ? subscriptionType.name : "";
  };

  const handleSubmit = async () => {
    try {
      if (modalType === "period") {
        if (!formData.name || !formData.dates) {
          message.error("Veuillez remplir tous les champs");
          return;
        }
        const periodData = {
          ...formData,
          start_date: dayjs(formData.dates[0]).format("MM-DD"),
          end_date: dayjs(formData.dates[1]).format("MM-DD"),
        };

        if (editingId) {
          await updateTariffPeriod(editingId, periodData);
          message.success("Période tarifaire mise à jour");
        } else {
          await createTariffPeriod(periodData);
          message.success("Période tarifaire ajoutée");
        }
      } else if (modalType === "subscription") {
        if (
          !formData.period_id ||
          !formData.subscription_type_id ||
          !formData.amount
        ) {
          message.error("Veuillez remplir tous les champs");
          return;
        }
        await updateTariffSubscriptions(formData.period_id, [
          {
            subscription_type_id: formData.subscription_type_id,
            amount: formData.amount,
          },
        ]);
        message.success("Abonnement tarifaire mis à jour");
      } else {
        if (
          !formData.period_id ||
          !formData.duration_hours ||
          !formData.price
        ) {
          message.error("Veuillez remplir tous les champs");
          return;
        }
        if (editingId) {
          await updateTariffRule(editingId, formData);
          message.success("Règle tarifaire mise à jour");
        } else {
          await createTariffRule(formData);
          message.success("Règle tarifaire ajoutée");
        }
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      message.error("Erreur lors de l'enregistrement");
      console.error(error);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setModalType(null);
    setFormData({});
    setEditingId(null);
  };

  const showModal = (type, data = {}) => {
    setModalType(type);

    if (type === "period") {
      if (data?.start_date && data?.end_date)
        setFormData({
          ...data,
          dates: [dayjs(data.start_date), dayjs(data.end_date)],
        });
      else setFormData(data);
    } else {
      setFormData(data);
    }

    setEditingId(data?.id || null);
    setIsModalVisible(true);
  };

  const periodColumns = [
    { title: "ID", dataIndex: "id", key: "id", sorter: (a, b) => a.id - b.id },
    { title: "Période", dataIndex: "name", key: "name" },
    {
      title: "Dates",
      render: (_, r) => `${r.start_date} - ${r.end_date}`,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showModal("period", record)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  const ruleColumns = [
    {
      title: "Période",
      dataIndex: "period_id",
      render: (period_id) => getPeriodNamebyId(period_id, data.tariffPeriods),
      sorter: (a, b) => a.id - b.id,
    },
    { title: "Description", dataIndex: "description" },
    {
      title: "Durée (h)",
      dataIndex: "duration_hours",
      align: "right",
      sorter: (a, b) => a.duration_hours - b.duration_hours,
    },
    {
      title: "Prix (Dh)",
      dataIndex: "price",
      align: "right",
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: "Compter dans le temps libre",
      dataIndex: "count_in_free_time",
      render: (v) => (v ? "Oui" : "Non"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showModal("rule", record)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  const subscriptionColumns = [
    {
      title: "Type d'abonnement",
      dataIndex: "subscription_type_id",
      render: (subscription_type_id) =>
        getSubscriptionTypeNamebyId(subscription_type_id),
      sorter: (a, b) => a.subscription_type_id - b.subscription_type_id,
    },
    {
      title: "Période",
      dataIndex: "period_id",
      render: (period_id) => getPeriodNamebyId(period_id, data.tariffPeriods),
      sorter: (a, b) => a.period_id - b.period_id,
    },
    {
      title: "Montant",
      dataIndex: "amount",
      align: "right",
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showModal("subscription", record)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  const renderModalContent = () => {
    if (modalType === "period") {
      return (
        <>
          <Input
            placeholder="Nom de la période"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mb-3"
          />
          <RangePicker
            format="MM-DD"
            value={formData.dates?.map((d) => (d ? dayjs(d, "MM-DD") : null))}
            onChange={(dates) =>
              setFormData({
                ...formData,
                dates: dates ? dates.map((d) => d.format("MM-DD")) : null,
              })
            }
            className="w-full"
          />
        </>
      );
    } else if (modalType === "subscription") {
      return (
        <>
          <Select
            placeholder="Type d'abonnement"
            options={data.subscriptionTypes?.map((s) => ({
              value: s.id,
              label: s.name,
            }))}
            value={formData.subscription_type_id}
            onChange={(v) =>
              setFormData({ ...formData, subscription_type_id: v })
            }
            className="w-full mb-3"
          />
          <Select
            placeholder="Période tarifaire"
            options={data.tariffPeriods.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
            value={formData.period_id}
            onChange={(v) => setFormData({ ...formData, period_id: v })}
            className="w-full mb-3"
          />
          <InputNumber
            placeholder="Montant"
            value={formData.amount}
            onChange={(v) => setFormData({ ...formData, amount: v })}
            className="w-full mb-3"
          />
        </>
      );
    }

    return (
      <>
        <Select
          placeholder="Période tarifaire"
          options={data.tariffPeriods.map((p) => ({
            value: p.id,
            label: p.name,
          }))}
          value={formData.period_id}
          onChange={(v) => setFormData({ ...formData, period_id: v })}
          disabled={!!editingId}
          className="w-full mb-3"
        />
        <InputNumber
          placeholder="Durée (heures)"
          value={formData.duration_hours}
          onChange={(v) => setFormData({ ...formData, duration_hours: v })}
          className="w-full mb-3"
        />
        <InputNumber
          placeholder="Prix"
          value={formData.price}
          onChange={(v) => setFormData({ ...formData, price: v })}
          className="w-full mb-3"
        />
        <Input
          placeholder="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          className="w-full mb-3"
        />
        <Select
          placeholder="Compter dans le temps libre"
          options={[
            { value: true, label: "Oui" },
            { value: false, label: "Non" },
          ]}
          value={formData.count_in_free_time}
          onChange={(v) => setFormData({ ...formData, count_in_free_time: v })}
          className="w-full"
        />
      </>
    );
  };

  return (
    <>
      <Card className="mb-6 shadow-sm">
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal("period")}
          >
            Nouvelle période
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => showModal("rule")}>
            Ajouter une règle
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal("subscription")}
          >
            Nouvel Abonnement
          </Button>
        </Space>
      </Card>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Périodes" key="1">
          <Spin spinning={loading}>
            <Table
              columns={periodColumns}
              dataSource={data.tariffPeriods}
              rowKey="id"
              className="mb-6"
            />
          </Spin>
        </TabPane>
        <TabPane tab="Règles" key="2">
          <Spin spinning={loading}>
            <Table
              columns={ruleColumns}
              dataSource={data.tariffRules.sort((a, b) => a.id - b.id)}
              rowKey="id"
            />
          </Spin>
          <CalculateurTemps />
        </TabPane>
        <TabPane tab="Abonnements" key="3">
          <Spin spinning={loading}>
            <Table
              columns={subscriptionColumns}
              dataSource={data.tariffSubscriptions}
              rowKey="id"
            />
          </Spin>
        </TabPane>
      </Tabs>

      <FormModal
        title={editingId ? "Modification" : "Nouvelle entrée"}
        visible={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
      >
        {renderModalContent()}
      </FormModal>
    </>
  );
}

export default TariffTab;
